"use client";

import { useTheme } from "next-themes";
import { Toaster as SonnerToaster, type ToasterProps } from "sonner";
import { useIsMobile } from "@/hooks/use-mobile";

// Colour lives on the per-type classes, never on the shared `toast` one — two
// competing !important backgrounds would be settled by stylesheet order, which
// Tailwind decides, not us. Sonner applies exactly one type class per toast, so
// each needs its own surface (including the neutral ones).
const NEUTRAL_SURFACE = "!bg-background/85 !text-foreground !border-border";

const toastClassNames = {
  toast:
    "!rounded-2xl !border !shadow-lg !backdrop-blur-xl !py-2.5 !pl-3.5 !pr-10 !gap-2",
  default: NEUTRAL_SURFACE,
  loading: NEUTRAL_SURFACE,
  info: NEUTRAL_SURFACE,
  // Success stays neutral: it confirms what you already expected, so it doesn't
  // need to compete for attention. Failures do.
  success: NEUTRAL_SURFACE,
  error:
    "!bg-red-50/95 !text-red-900 !border-red-200 [&_[data-icon]]:!text-red-600 " +
    "dark:!bg-red-950/85 dark:!text-red-100 dark:!border-red-900/50 dark:[&_[data-icon]]:!text-red-400",
  warning:
    "!bg-amber-50/95 !text-amber-900 !border-amber-200 [&_[data-icon]]:!text-amber-600 " +
    "dark:!bg-amber-950/85 dark:!text-amber-100 dark:!border-amber-900/50 dark:[&_[data-icon]]:!text-amber-400",
  title: "!text-[13px] !font-medium !leading-snug",
  description: "!text-[12px] !opacity-80 !leading-snug",
  icon: "!size-4 shrink-0",
  closeButton:
    "!left-auto !right-2 !top-1/2 !-translate-y-1/2 !translate-x-0 !border !border-current/25 !bg-transparent !text-current !opacity-60 hover:!opacity-100 hover:!bg-current/10 !size-7 !min-h-7 !min-w-7 !rounded-lg !transition-all",
};

export function Toaster({
  position,
  closeButton = true,
  mobileOffset,
  offset,
  ...props
}: ToasterProps) {
  const { resolvedTheme } = useTheme();
  const isMobile = useIsMobile();

  const theme = (resolvedTheme === "dark" ? "dark" : "light") as ToasterProps["theme"];

  return (
    <SonnerToaster
      theme={theme}
      closeButton={closeButton}
      // Top-centre everywhere. Feedback belongs near the controls that trigger
      // it — those live in the header — and a bottom-right toast is diagonally
      // opposite the button you just pressed.
      position={position ?? "top-center"}
      // Near the top edge — the top bar's centre is empty (title left,
      // actions right), so the toast can float over it without covering
      // page content below.
      offset={offset ?? { top: "0.75rem" }}
      mobileOffset={
        mobileOffset ?? {
          top: "max(0.5rem, env(safe-area-inset-top))",
        }
      }
      style={{
        ...(isMobile ? { ["--width" as string]: "min(92vw, 420px)" } : {}),
        zIndex: 99999,
      }}
      toastOptions={{
        classNames: toastClassNames,
      }}
      {...props}
    />
  );
}
